import type { VisualizationSpec } from "vega-embed";

type SpecRecord = Record<string, unknown>;

const asRecord = (value: unknown): SpecRecord =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as SpecRecord) : {};

/** How Vega-Lite words an aggregate in its own default title ("Sum of Amount"). */
const AGGREGATE_WORDS: Record<string, string> = {
  sum: "Sum",
  mean: "Average",
  average: "Average",
  median: "Median",
  min: "Minimum",
  max: "Maximum",
  distinct: "Distinct count",
  count: "Count",
};

/**
 * The business name for a field id: "Region.Region" and "Orders.Region" both read "Region" (the
 * table prefix is dropped, the column is the name). Escaped dots and underscores read as spaces.
 */
export function fieldBusinessName(field: string): string {
  const flat = field.replace(/\\\./g, ".");
  const column = flat.includes(".") ? flat.slice(flat.lastIndexOf(".") + 1) : flat;
  return (column || flat).replace(/_/g, " ").trim();
}

const aggregateTitle = (aggregate: unknown, name: string): string => {
  if (typeof aggregate !== "string") return name;
  const word = AGGREGATE_WORDS[aggregate];
  return word ? `${word} of ${name}` : name;
};

/**
 * "Table.Column" with no spaces around the dot: the server also titles renamed fields this way
 * (field "region", title "Region.Region"). Real titles such as "Rev. growth" don't match.
 */
const ID_TITLE = /^\w[\w ]*(?:\\?\.\w[\w ]*)+$/;

const isIdTitle = (title: unknown): title is string => typeof title === "string" && ID_TITLE.test(title);

/**
 * A title the reader would see as a raw id: none at all, or the field id itself. The server's
 * compiled specs title every channel, axis and legend with the field id ("Region.Region"), so an
 * id-shaped title counts as no title of the spec's own.
 */
const isRawTitle = (title: unknown, field: string): boolean =>
  title === undefined ||
  title === field ||
  title === field.replace(/\\\./g, ".") ||
  isIdTitle(title);

/** A plain identifier such as "order_date": no table prefix, so only humanising applies. */
const PLAIN_FIELD = /^[a-z][a-z0-9_]*$/;

const capitalise = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** An `axis` / `legend` block whose title is the raw field id, titled by the business name. */
const titleGuide = (guide: unknown, field: string, title: string): unknown => {
  const source = asRecord(guide);
  if (guide === null || !("title" in source) || !isRawTitle(source.title, field)) return guide;
  return { ...source, title };
};

/** A channel definition with a table-qualified field or id-shaped title, titled by its business name, unless the spec set a real title. */
const titleDefinition = (definition: unknown): unknown => {
  const source = asRecord(definition);
  const field = source.field;
  if (typeof field !== "string") return definition;
  const idTitle = isIdTitle(source.title) ? source.title : undefined;
  const plain = !field.includes(".") && PLAIN_FIELD.test(field) && isRawTitle(source.title, field);
  if (!field.includes(".") && !idTitle && !plain) return definition;
  const name = plain && !idTitle ? capitalise(fieldBusinessName(field)) : fieldBusinessName(field.includes(".") ? field : (idTitle as string));
  const title = aggregateTitle(source.aggregate, name);
  const next: SpecRecord = { ...source };
  if (isRawTitle(source.title, field)) next.title = title;
  if (source.axis !== undefined) next.axis = titleGuide(source.axis, field, title);
  if (source.legend !== undefined) next.legend = titleGuide(source.legend, field, title);
  return next;
};

const titleChannel = (channel: unknown): unknown =>
  Array.isArray(channel) ? channel.map(titleDefinition) : titleDefinition(channel);

const titleEncoding = (encoding: unknown): unknown => {
  const source = asRecord(encoding);
  return Object.fromEntries(Object.entries(source).map(([name, channel]) => [name, titleChannel(channel)]));
};

const NESTED = ["layer", "hconcat", "vconcat", "concat"];

/** A plain-Vega `legends` / `axes` entry whose title is an id-shaped "Table.Column": titled by the column's business name. */
const titleVegaGuide = (guide: unknown): unknown => {
  const source = asRecord(guide);
  if (!isIdTitle(source.title)) return guide;
  return { ...source, title: fieldBusinessName(source.title) };
};

/** Plain Vega (not Vega-Lite): legend and axis titles, also inside group marks. */
const titleVega = (spec: SpecRecord): SpecRecord => {
  const next: SpecRecord = { ...spec };
  for (const key of ["legends", "axes"]) {
    if (Array.isArray(spec[key])) next[key] = (spec[key] as unknown[]).map(titleVegaGuide);
  }
  if (Array.isArray(spec.marks)) next.marks = (spec.marks as unknown[]).map((mark) => titleVega(asRecord(mark)));
  return next;
};

const titleUnit = (spec: SpecRecord): SpecRecord => {
  const next: SpecRecord = { ...titleVega(spec) };
  if (spec.encoding !== undefined) next.encoding = titleEncoding(spec.encoding);
  for (const key of NESTED) {
    if (Array.isArray(spec[key])) next[key] = (spec[key] as unknown[]).map((child) => titleUnit(asRecord(child)));
  }
  if (spec.spec !== undefined) next.spec = titleUnit(asRecord(spec.spec));
  return next;
};

/**
 * Every encoded field shows its business name in axis titles, legend titles and tooltips, not the
 * raw "Table.Column" id. A title the spec sets itself is kept.
 */
export const withReadableFieldTitles = (spec: VisualizationSpec): VisualizationSpec =>
  titleUnit(spec as SpecRecord) as VisualizationSpec;
